import { createFileRoute, useParams } from "@tanstack/react-router";
import { useEffect, useMemo, useState, type FormEvent } from "react";
import { useAuthState } from "@tubo/store";
import { Button, buttonVariants } from "@tubo/ui/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@tubo/ui/components/ui/card";
import { Input } from "@tubo/ui/components/ui/input";
import { Label } from "@tubo/ui/components/ui/label";
import { Download, FileText, Link2, Loader2, Send, TriangleAlert } from "lucide-react";
import {
  useDownloadDocShareLinkFile,
  usePreviewDocShareLinkFile,
  useResolveDocShareLink,
} from "@tubo/api-models/hooks/doc-share-link-access-api.hooks";
import { RightEnum } from "@tubo/api-models/business/rbac/rbac.enums";
import { useUiTranslation } from "@tubo/ui/i18n";
import {
  useCreateDocCenterAccessRequest,
} from "@client/components/doc-center/doc-center-collaborator-api";

export const Route = createFileRoute("/share/doc/$token")({
  component: DocShareLandingPage,
});

function DocShareLandingPage() {
  const { t } = useUiTranslation("auxiliaryPages");
  const { token } = useParams({ from: "/share/doc/$token" });
  const { isAuthenticated } = useAuthState();
  const [accessCodeInput, setAccessCodeInput] = useState("");
  const [accessCode, setAccessCode] = useState<string>();
  const [selectedFileId, setSelectedFileId] = useState<string>();
  const [requestRight, setRequestRight] = useState<RightEnum.View | RightEnum.Edit>(RightEnum.Edit);
  const [requestMessage, setRequestMessage] = useState("");
  const resolveQuery = useResolveDocShareLink(
    token,
    accessCode ? { accessCode } : undefined,
    { query: { retry: false } },
  );
  const data = resolveQuery.data;
  const files = useMemo(() => (data?.resourceKind === "folder" ? data.files ?? [] : []), [data]);
  const fileId = data?.resourceKind === "file" ? data.resourceId : selectedFileId;
  const selectedFile = files.find((file) => file.id === fileId);
  const previewQuery = usePreviewDocShareLinkFile(
    token,
    fileId ? { fileId, accessCode } : undefined,
    { query: { enabled: Boolean(data) && !data?.requiresAccessCode && Boolean(fileId), retry: false } },
  );
  const downloadQuery = useDownloadDocShareLinkFile(
    token,
    fileId ? { fileId, accessCode } : undefined,
    { query: { enabled: isAuthenticated && Boolean(data?.canDownload) && Boolean(fileId), retry: false } },
  );
  const requestMutation = useCreateDocCenterAccessRequest();
  const [previewUrl, setPreviewUrl] = useState<string>();

  useEffect(() => {
    if (data?.resourceKind === "file") {
      setSelectedFileId(data.resourceId);
      return;
    }
    if (files.length > 0 && !files.some((file) => file.id === selectedFileId)) {
      setSelectedFileId(files[0].id);
    }
  }, [data, files, selectedFileId]);

  useEffect(() => {
    if (!previewQuery.data) {
      setPreviewUrl(undefined);
      return;
    }
    const url = URL.createObjectURL(previewQuery.data);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [previewQuery.data]);

  const submitAccessCode = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (/^\d{4,6}$/.test(accessCodeInput)) setAccessCode(accessCodeInput);
  };

  const submitAccessRequest = async () => {
    if (!data || !isAuthenticated) return;
    try {
      await requestMutation.mutateAsync({
        resource: data.resourceKind === "file" ? "files" : "folders",
        id: data.resourceId,
        data: {
          token,
          right: requestRight,
          message: requestMessage.trim() || undefined,
          accessCode,
        },
      });
      setRequestMessage("");
    } catch {
      // 页面保留当前输入，让用户可以修正访问码或申请内容后重试。
    }
  };

  const errorText = resolveQuery.isError && accessCode
    ? t("docShare.invalidCode")
    : t("docShare.invalidLink");

  return (
    <div className="flex min-h-screen items-center justify-center bg-surface p-6">
      <Card className="w-full max-w-lg">
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><Link2 className="size-5 text-primary" />{t("docShare.title")}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {resolveQuery.isLoading ? (
            <div className="flex justify-center py-8"><Loader2 className="size-5 animate-spin text-muted-foreground" /></div>
          ) : resolveQuery.isError || !data ? (
            <div className="space-y-3">
              <div className="flex items-center gap-2 rounded-md border border-destructive/30 bg-destructive/5 p-4 text-sm text-destructive">
                <TriangleAlert className="size-4 shrink-0" />{errorText}
              </div>
              {(accessCode || !isAuthenticated) && (
                <AccessCodeForm value={accessCodeInput} onChange={setAccessCodeInput} onSubmit={submitAccessCode} />
              )}
            </div>
          ) : data.requiresAccessCode ? (
            <AccessCodeForm value={accessCodeInput} onChange={setAccessCodeInput} onSubmit={submitAccessCode} />
          ) : (
            <>
              <div>
                <p className="font-medium">{data.resourceName}</p>
                <p className="mt-1 text-sm text-muted-foreground">
                  {t("docShare.permission.label", { value: data.right === "E" ? t("docShare.permission.edit") : t("docShare.permission.view") })}
                </p>
              </div>
              {data.watermarkText && (
                <div className="rounded-md border border-amber-300/50 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                  {data.watermarkText}
                </div>
              )}
              {data.resourceKind === "folder" && <SharedFileList files={files} fileId={fileId} onSelect={setSelectedFileId} />}
              {fileId && (
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    {data.canDownload && isAuthenticated ? (
                      <Button className="flex-1" disabled={downloadQuery.isLoading || !downloadQuery.data} onClick={() => {
                        if (!downloadQuery.data) return;
                        const url = URL.createObjectURL(downloadQuery.data);
                        const anchor = document.createElement("a");
                        anchor.href = url;
                        anchor.download = selectedFile?.name ?? data.resourceName;
                        anchor.click();
                        URL.revokeObjectURL(url);
                      }}>
                        {downloadQuery.isLoading ? <Loader2 className="size-4 animate-spin" /> : <Download className="size-4" />}
                        {t("docShare.download")}
                      </Button>
                    ) : <span className="flex-1 text-xs text-muted-foreground">{data.isVisitor || data.canDownload === false ? t("docShare.visitorPreview") : t("docShare.loginToDownload")}</span>}
                    {previewUrl && <a className={buttonVariants({ variant: "outline" })} href={previewUrl} target="_blank" rel="noreferrer">{t("docShare.newWindowPreview")}</a>}
                  </div>
                  {previewUrl && <iframe title={t("docShare.previewTitle", { name: selectedFile?.name ?? data.resourceName })} src={previewUrl} className="h-96 w-full rounded-md border border-border-muted" />}
                  {previewQuery.isError && <p className="text-sm text-destructive">{t("docShare.loadFailed")}</p>}
                </div>
              )}
              {isAuthenticated && (
                <AccessRequestForm
                  right={requestRight}
                  message={requestMessage}
                  pending={requestMutation.isPending}
                  submitted={requestMutation.isSuccess}
                  failed={requestMutation.isError}
                  onRightChange={setRequestRight}
                  onMessageChange={setRequestMessage}
                  onSubmit={() => void submitAccessRequest()}
                />
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function AccessCodeForm({
  value,
  onChange,
  onSubmit,
}: {
  value: string;
  onChange: (value: string) => void;
  onSubmit: (event: FormEvent<HTMLFormElement>) => void;
}) {
  const { t } = useUiTranslation("auxiliaryPages");
  return (
    <form className="space-y-3" onSubmit={onSubmit}>
      <div className="space-y-1"><Label htmlFor="doc-share-access-code">{t("docShare.accessCode")}</Label><Input id="doc-share-access-code" value={value} onChange={(event) => onChange(event.target.value.replace(/\D/g, "").slice(0, 6))} placeholder={t("docShare.accessCodePlaceholder")} inputMode="numeric" /></div>
      <Button className="w-full" type="submit" disabled={!/^\d{4,6}$/.test(value)}>{t("docShare.continuePreview")}</Button>
    </form>
  );
}

function SharedFileList({
  files,
  fileId,
  onSelect,
}: {
  files: Array<{ id: string; name: string; right: "V" | "E" }>;
  fileId?: string;
  onSelect: (id: string) => void;
}) {
  const { t } = useUiTranslation("auxiliaryPages");
  return (
    <div className="space-y-2 rounded-md border border-border-muted p-3">
      <p className="text-sm font-medium">{t("docShare.folderFiles")}</p>
      {files.length === 0 ? <p className="text-sm text-muted-foreground">{t("docShare.folderEmpty")}</p> : (
        <div className="space-y-1">{files.map((file) => (
          <button key={file.id} type="button" className={`flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm ${file.id === fileId ? "bg-primary/10 text-primary" : "hover:bg-surface-2"}`} onClick={() => onSelect(file.id)}>
            <FileText className="size-4 shrink-0" /><span className="min-w-0 flex-1 truncate">{file.name}</span><span className="text-xs text-muted-foreground">{file.right === "E" ? t("docShare.permission.edit") : t("docShare.permission.view")}</span>
          </button>
        ))}</div>
      )}
    </div>
  );
}

function AccessRequestForm({
  right,
  message,
  pending,
  submitted,
  failed,
  onRightChange,
  onMessageChange,
  onSubmit,
}: {
  right: RightEnum.View | RightEnum.Edit;
  message: string;
  pending: boolean;
  submitted: boolean;
  failed: boolean;
  onRightChange: (right: RightEnum.View | RightEnum.Edit) => void;
  onMessageChange: (message: string) => void;
  onSubmit: () => void;
}) {
  const { t } = useUiTranslation("auxiliaryPages");
  return (
    <div className="space-y-3 rounded-md border border-border-muted p-3">
      <div className="flex items-center gap-2 text-sm font-medium"><Send className="size-4 text-primary" />{t("docShare.requestPermission")}</div>
      <div className="flex gap-2"><Button type="button" size="sm" variant={right === RightEnum.View ? "default" : "outline"} onClick={() => onRightChange(RightEnum.View)}>{t("docShare.view")}</Button><Button type="button" size="sm" variant={right === RightEnum.Edit ? "default" : "outline"} onClick={() => onRightChange(RightEnum.Edit)}>{t("docShare.edit")}</Button></div>
      <Input aria-label={t("docShare.requestMessagePlaceholder")} value={message} onChange={(event) => onMessageChange(event.target.value)} placeholder={t("docShare.requestMessagePlaceholder")} disabled={pending} />
      {submitted && <p role="status" className="text-sm text-muted-foreground">{t("docShare.requestSubmitted")}</p>}
      {failed && <p role="alert" className="text-sm text-destructive">{t("docShare.requestFailed")}</p>}
      <Button className="w-full" size="sm" onClick={onSubmit} disabled={pending}>{pending ? <Loader2 className="size-4 animate-spin" /> : <Send className="size-4" />}{t("docShare.submitRequest")}</Button>
    </div>
  );
}
